import React, { useState, useEffect, useMemo } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import Header from '../components/layout/Header';
import Footer from '../components/layout/Footer';
import CartDrawer from '../components/cart/CartDrawer';
import ProductCard from '../components/products/ProductCard';
import { products, categories, siteImages } from '../data/mock';
import { ChevronDown, LayoutGrid, Music2 } from 'lucide-react';

const ShopPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [sortBy, setSortBy] = useState('featured');

  const selectedCategory = searchParams.get('category') || '';

  useEffect(() => {
    document.title = 'Shop Musical Instruments & Music Gear | Pitch Craft Gear';
  }, []);

  const filteredProducts = useMemo(() => {
    let result = [...products];
    if (selectedCategory) {
      result = result.filter(p => p.categorySlug === selectedCategory);
    }
    switch (sortBy) {
      case 'price-low': result.sort((a, b) => a.price - b.price); break;
      case 'price-high': result.sort((a, b) => b.price - a.price); break;
      case 'name': result.sort((a, b) => a.name.localeCompare(b.name)); break;
      default: break;
    }
    return result;
  }, [selectedCategory, sortBy]);

  const handleCategoryChange = (categorySlug) => {
    if (categorySlug) setSearchParams({ category: categorySlug });
    else setSearchParams({});
  };

  const currentCategory = categories.find(c => c.slug === selectedCategory);

  return (
    <div className="min-h-screen bg-dark-50 text-dark-700">
      <Header />
      <CartDrawer />

      <main>
        {/* Light banner header */}
        <section className="bg-white border-b border-dark-100">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 grid lg:grid-cols-2 gap-8 items-center">
            <div>
              <nav className="text-sm text-dark-400 mb-3">
                <Link to="/" className="hover:text-athletic-600">Home</Link>
                <span className="mx-2">/</span>
                <span className="text-dark-700">{currentCategory ? currentCategory.name : 'Shop All'}</span>
              </nav>
              <h1 className="font-display text-3xl sm:text-5xl font-semibold tracking-tight text-dark-900">
                {currentCategory ? currentCategory.name : 'Shop All Instruments'}
              </h1>
              <p className="mt-4 text-dark-600 max-w-xl leading-relaxed">
                {currentCategory
                  ? currentCategory.description
                  : 'A tightly curated collection of instruments and music gear \u2014 strings, drums, woodwinds, studio kit and the accessories that keep you playing.'}
              </p>
            </div>
            <div className="hidden lg:block">
              <img src={siteImages.shopBanner} alt="" className="w-full h-56 object-cover rounded-2xl shadow-soft" />
            </div>
          </div>
        </section>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
          <div className="flex flex-col lg:flex-row gap-8">
            {/* Sidebar */}
            <aside className="lg:w-64 flex-shrink-0">
              <div className="lg:sticky lg:top-28">
                <h2 className="flex items-center gap-2 font-display text-lg font-semibold text-dark-900 mb-4 pb-3 border-b border-dark-200">
                  <Music2 className="w-4 h-4 text-athletic-600" /> Categories
                </h2>
                <div className="flex lg:flex-col gap-2 overflow-x-auto lg:overflow-visible pb-2 lg:pb-0">
                  <button
                    onClick={() => handleCategoryChange('')}
                    className={`text-left px-4 py-2.5 rounded-full text-sm font-medium whitespace-nowrap transition-colors flex items-center justify-between gap-3 ${!selectedCategory ? 'bg-athletic-500 text-white' : 'bg-white border border-dark-200 text-dark-700 hover:border-athletic-400 hover:text-athletic-700'}`}
                  >
                    All Products <span className="text-xs opacity-80">{products.length}</span>
                  </button>
                  {categories.map((cat) => (
                    <button
                      key={cat.id}
                      onClick={() => handleCategoryChange(cat.slug)}
                      className={`text-left px-4 py-2.5 rounded-full text-sm font-medium whitespace-nowrap transition-colors flex items-center justify-between gap-3 ${selectedCategory === cat.slug ? 'bg-athletic-500 text-white' : 'bg-white border border-dark-200 text-dark-700 hover:border-athletic-400 hover:text-athletic-700'}`}
                    >
                      <span>{cat.name}</span> <span className="text-xs opacity-80">{cat.productCount}</span>
                    </button>
                  ))}
                </div>
              </div>
            </aside>

            {/* Content */}
            <div className="flex-1">
              {/* Toolbar */}
              <div className="flex items-center justify-between gap-3 mb-6 pb-4 border-b border-dark-100">
                <p className="flex items-center gap-2 text-sm text-dark-600">
                  <LayoutGrid className="w-4 h-4 text-athletic-600" />
                  <span className="font-semibold text-dark-900">{filteredProducts.length}</span> product{filteredProducts.length !== 1 ? 's' : ''}
                </p>
                <div className="relative">
                  <select
                    value={sortBy}
                    onChange={(e) => setSortBy(e.target.value)}
                    className="appearance-none bg-white border border-dark-300 text-dark-700 text-sm rounded-full px-4 py-2.5 pr-9 focus:outline-none focus:ring-2 focus:ring-athletic-500/60 cursor-pointer"
                  >
                    <option value="featured">Featured</option>
                    <option value="price-low">Price: Low to High</option>
                    <option value="price-high">Price: High to Low</option>
                    <option value="name">Name: A-Z</option>
                  </select>
                  <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-dark-400 pointer-events-none" />
                </div>
              </div>

              {/* Grid */}
              {filteredProducts.length === 0 ? (
                <div className="bg-white rounded-2xl p-12 text-center border border-dark-100">
                  <p className="text-dark-600">No products found in this category.</p>
                </div>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-6">
                  {filteredProducts.map((product) => (
                    <ProductCard key={product.id} product={product} />
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default ShopPage;
